import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, Observable, Subscription, firstValueFrom } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import {
    AutoImportWebHookEventTypes,
    Contact,
    IntegrationConfig,
    LayerImportConfig,
} from '@/core/models/IntegrationEntity';
import { Layer } from '@/core/models/layer';
import { TranslateService } from '@ngx-translate/core';
import { TypedSelectButtonChangeEvent } from '@/core/types/angular-primeng-api';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { ResourceImportRequestForm, ResourceTypes } from '@/core/models/integration-types';
import { Tile } from '@/core/models/tile';
import { ToastService } from '@/core/services/toast.service';
import { ApiErrorHandlerService } from '@/core/services/api-error-handler.service';
import { TypeGuard } from '@/core/types/type-guard';
import { ReauthError } from '@/core/types/reauth-error';
import { ServiceResource } from '../../../core/models/IntegrationEntity';

interface Resource {
    name: string;
    value: string;
}

interface ImportItem {
    name: string;
    value: ResourceTypes;
    disabled?: boolean;
    isConflicting?: boolean;
    conflictingLayerName?: string;
}

interface ImportMode {
    name: string;
    value: ImportModeValues;
}

enum ImportModeValues {
    AUTOMATIC = 'automatic',
    MANUAL = 'manual',
}

interface ItemConfig {
    translationKey: string;
    value: ResourceTypes;
}

class ItemConfigHelper {
    public static getInit(): ItemConfig[] {
        return [
            {
                translationKey: '',
                value: ResourceTypes.CONTACTS,
            },
            {
                translationKey: '',
                value: ResourceTypes.SERVICES,
            },
        ];
    }

    public static updateTranslationKeys(itemConfig: ItemConfig[]): void {
        itemConfig[0].translationKey = 'int_customer_label';
        itemConfig[1].translationKey = 'int_services_label';
    }

    static updateByIntegrationConfig(
        itemConfig: ItemConfig[],
        integrationConfig: IntegrationConfig | undefined,
    ): ItemConfig[] {
        // TODO - bad code structure, this needs initializeTranslations to be called afterwards to work correctly
        //        not part of this method, because of potential duplicate call in integration subscription
        if (integrationConfig && integrationConfig.properties?.supportedResourceTypes) {
            if (!integrationConfig.properties.supportedResourceTypes.includes(ResourceTypes.CONTACTS)) {
                itemConfig = itemConfig.filter((item) => item.value !== ResourceTypes.CONTACTS);
            }
            if (!integrationConfig.properties.supportedResourceTypes.includes(ResourceTypes.SERVICES)) {
                itemConfig = itemConfig.filter((item) => item.value !== ResourceTypes.SERVICES);
            }
        }
        return itemConfig;
    }
}

@Component({
    selector: 'app-integration-import',
    templateUrl: './integration-import.component.html',
    styleUrls: ['./integration-import.component.scss'],
    standalone: false,
})
export class IntegrationImportComponent implements OnInit, OnDestroy {
    @Input() serviceIdentifier: string = '';
    @Input() allTiles: Tile[] = [];
    @Input() currentLayerName: string = '';
    @Input() layers: Layer[] = [];
    @Output() import = new EventEmitter<void>();
    @Output() showDialogChange = new EventEmitter<boolean>();
    ImportModeValues = ImportModeValues;
    items: ImportItem[] = [];
    importModes: ImportMode[] = [];
    resources: Resource[] = [];
    selectedResources: Resource[] = [];
    totalResourceCount: number = 0;
    loading = false;
    showResources = false;
    integrationConfig?: IntegrationConfig;
    private ITEM_CONFIGS: ItemConfig[];
    private destroy$ = new Subject<void>();
    private _showDialog = false;
    private _enableAutoImport = false;
    private _selectedItem: ImportItem | null = null;
    private _importMode: ImportMode | null = null;
    private subscriptions = new Subscription();

    /**
     * Returns the count of new (importable) resources
     * This is always in sync with the resource array
     */
    get newResourceCount(): number {
        return this.resources.length;
    }

    // Cache for the currently selected item's conflict status
    private _selectedItemConflictCache: boolean | null = null;

    /**
     * Checks if the currently selected item has auto-import conflicts
     */
    private get selectedItemHasConflict(): boolean {
        if (!this.selectedItem) return false;

        // Use cached result if available
        if (this._selectedItemConflictCache !== null) {
            return this._selectedItemConflictCache;
        }

        // Calculate and cache result
        this._selectedItemConflictCache = this.getItemConflictInfo(this.selectedItem) !== null;
        return this._selectedItemConflictCache;
    }

    /**
     * Common validation state to do with loading state, item conflict (auto-import) and selected item presence
     */
    private get baseValidationState(): { hasSelectedItem: boolean; isLoading: boolean; itemIsConflicting: boolean } {
        return {
            hasSelectedItem: !!this.selectedItem,
            isLoading: this.loading,
            itemIsConflicting: this.selectedItemHasConflict,
        };
    }

    /**
     * Auto-import active state - used by multiple getters
     */
    get autoImportActiveState(): boolean {
        const state = this.baseValidationState;
        return (
            this.enableAutoImport &&
            state.hasSelectedItem &&
            !state.itemIsConflicting &&
            this.currentResourceTypeSupportsAutoSync
        );
    }

    get selectedItem(): ImportItem | null {
        return this._selectedItem;
    }

    set selectedItem(value: ImportItem | null) {
        // Invalidate conflict cache when selectedItem changes
        if (this._selectedItem !== value) {
            this._selectedItemConflictCache = null;
        }
        this._selectedItem = value;

        // Update import mode labels when the selected item changes
        this.updateImportModeLabels();
    }

    get importMode(): ImportMode | null {
        return this._importMode;
    }

    set importMode(value: ImportMode | null) {
        this._importMode = value;
    }

    @Input()
    get showDialog(): boolean {
        return this._showDialog;
    }

    set showDialog(value: boolean | undefined | null) {
        this._showDialog = value ?? false;
        this.showDialogChange.emit(this._showDialog);
    }

    get enableAutoImport(): boolean {
        return this._enableAutoImport;
    }

    set enableAutoImport(value: boolean) {
        this._enableAutoImport = value;
    }

    get needsReauth(): boolean {
        return this.integrationConfig?.status === 'active' && this.integrationConfig?.authStatus === 'invalid';
    }

    /**
     * This would be true when the integration's auth could not be verified due to rate limiting. Unlike needsReauth,
     * this is a temporary condition - the same import-blocking behavior applies, but no reauth prompt
     * should be shown for it.
     */
    get isRateLimited(): boolean {
        return this.integrationConfig?.status === 'active' && this.integrationConfig?.authStatus === 'rate-limited';
    }

    /** True while import should be blocked because the integration's auth can't currently be used. */
    get importBlockedByAuth(): boolean {
        return this.needsReauth || this.isRateLimited;
    }

    get importSrcDropDownDisabled(): boolean {
        const state = this.baseValidationState;
        return this.importBlockedByAuth || state.isLoading || this.autoImportActiveState;
    }

    get importModeDisabled(): boolean {
        return this.importBlockedByAuth || !this.selectedItem || this.loading;
    }

    get resourceListDisabled(): boolean {
        return this.importBlockedByAuth || this.resources.length === 0 || this.loading;
    }

    get refreshDisabled(): boolean {
        return this.importBlockedByAuth || this.loading;
    }

    get importDisabled(): boolean {
        const state = this.baseValidationState;
        if (this.importBlockedByAuth || state.isLoading || !state.hasSelectedItem) return true;

        return this.isImportModeDisabled();
    }

    /**
     * True when the selected resource type is listed in the integration's autoSyncResourceTypes.
     */
    get currentResourceTypeSupportsAutoSync(): boolean {
        const autoSyncTypes = this.integrationConfig?.properties?.autoSyncResourceTypes ?? [];
        return !!this.selectedItem && autoSyncTypes.includes(this.selectedItem.value as ResourceTypes);
    }

    get autoImportSwitchDisabled(): boolean {
        const state = this.baseValidationState;
        return (
            this.importBlockedByAuth ||
            !state.hasSelectedItem ||
            state.isLoading ||
            state.itemIsConflicting ||
            !this.currentResourceTypeSupportsAutoSync
        );
    }

    private isImportModeDisabled(): boolean {
        const isAutomatic = this.importMode?.value === ImportModeValues.AUTOMATIC;
        return isAutomatic ? !this.enableAutoImport : this.selectedResources.length === 0;
    }

    private get currentLayerId(): number {
        return parseInt(this.route.snapshot.params['id']);
    }

    constructor(
        private readonly integrationService: IntegrationService,
        private route: ActivatedRoute,
        private tr: TranslateService,
        private toastService: ToastService,
        private apiErrorHandler: ApiErrorHandlerService,
        private cdr: ChangeDetectorRef,
    ) {
        this.ITEM_CONFIGS = ItemConfigHelper.getInit();
    }

    ngOnInit(): void {
        ItemConfigHelper.updateTranslationKeys(this.ITEM_CONFIGS);

        this.tr.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.initializeTranslations();
        });
        this.initializeTranslations();

        this.subscriptions.add(
            this.integrationService.integrations$.subscribe((integrations: IntegrationConfig[]) => {
                this.integrationConfig = integrations.find(
                    (i: IntegrationConfig) => i.service === this.serviceIdentifier,
                );
                this.updateResourceOptions();
                this.initializeTranslations();
            }),
        );
    }

    private updateResourceOptions(): void {
        this.ITEM_CONFIGS = ItemConfigHelper.updateByIntegrationConfig(this.ITEM_CONFIGS, this.integrationConfig);
    }

    private initializeTranslations(): void {
        // Show the automatic tab if the integration declares any auto-sync resource types at all.
        // Whether the toggle is enabled/disabled for the *selected* resource type is handled separately
        // via `currentResourceTypeSupportsAutoSync` and `autoImportSwitchDisabled`.
        const hasAnyAutoSyncSupport = (this.integrationConfig?.properties?.autoSyncResourceTypes?.length ?? 0) > 0;

        if (this.importModes.length === 0) {
            this.importModes = [
                {
                    name: '',
                    value: ImportModeValues.MANUAL,
                },
            ];
            this.importMode = this.importModes[0];
        }
        if (hasAnyAutoSyncSupport && this.importModes.length === 1) {
            this.importModes.push({
                name: '',
                value: ImportModeValues.AUTOMATIC,
            });
        }
        if (!hasAnyAutoSyncSupport && this.importModes.length > 1) {
            this.importModes.splice(1, 1);
        }

        const translationKeys = [
            'int_automatic_import_label_default',
            'int_manual_import_label',
            ...this.ITEM_CONFIGS.map((config) => config.translationKey),
        ];

        this.tr
            .get(translationKeys)
            .pipe(takeUntil(this.destroy$))
            .subscribe((translations) => {
                // import modes
                this.importModes[0].name = translations['int_manual_import_label'];
                if (this.importModes.length > 1) {
                    this.importModes[1].name = translations['int_automatic_import_label_default'];
                }

                // import data source items
                this.items = this.ITEM_CONFIGS.map((config) => ({
                    name: translations[config.translationKey],
                    value: config.value,
                }));

                // Safety guard check that verifies the user has valid authentication
                if (this.items.length === 1 && !this.importBlockedByAuth) {
                    this.selectedItem = this.items[0];
                    this.onItemSelected({ value: this.items[0] });
                }

                this.updateImportModeLabels();
                this.cdr.detectChanges();
            });
    }

    private updateImportModeLabels(): void {
        if (this.selectedItem?.name) {
            const data = this.selectedItem.name;

            this.tr
                .get('int_automatic_import_label', { data })
                .pipe(takeUntil(this.destroy$))
                .subscribe((translation) => {
                    if (this.importModes.length < 2) return;
                    const currentSelection = this.importMode;

                    this.importModes[1].name = translation;

                    if (currentSelection && currentSelection.value === ImportModeValues.AUTOMATIC) {
                        this.importMode = null;

                        void Promise.resolve().then(() => {
                            this.importMode = this.importModes[1];
                            this.cdr.detectChanges();
                        });
                    } else {
                        this.cdr.detectChanges();
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    onShow(): void {
        // states are set here, as switching between layers does not re-init the component
        this.refreshImportState();
    }

    /**
     * Refreshes the import state by updating disabled status and auto-import configuration
     */
    private refreshImportState(): void {
        this.updateImportSrcDisabledStatus();
        this.setupAutoImportFromRoute();
    }

    /**
     * Updates the disabled status and conflict info for import source items (computed once, not in template)
     */
    private updateImportSrcDisabledStatus(): void {
        this.items = this.items.map((item) => {
            const conflictInfo = this.getItemConflictInfo(item);
            const hasConflict = conflictInfo !== null;

            return {
                ...item,
                disabled: hasConflict,
                isConflicting: hasConflict,
                conflictingLayerName: conflictInfo?.layerName || '',
            };
        });
    }

    private setupAutoImportFromRoute(): void {
        if (this.importBlockedByAuth) return;

        const autoImportLayers = this.getAutoImportLayers();
        if (autoImportLayers.length === 0) return;

        // Check if the current layer has auto-import enabled for any source
        const layerWithAutoImport = autoImportLayers.find(
            (layer) => layer.autoImportEnabled && layer.layerId === this.currentLayerId,
        );

        if (layerWithAutoImport) {
            // Pre-select the source that has auto-import enabled
            const importType = EventTypeMapper.convertWebHookEventTypeToImportType(
                layerWithAutoImport.importEventType as AutoImportWebHookEventTypes,
            );
            const sourceItem = this.items.find((item) => item.value === importType);

            if (sourceItem) {
                this.selectedItem = sourceItem;
                this.enableAutoImport = true;
                // load the resources as manual import mode is the default
                this.loading = true;
                this.loadResourcesForDataSourceObservable(this.selectedItem.value)
                    .pipe(
                        takeUntil(this.destroy$),
                        finalize(() => (this.loading = false)),
                    )
                    .subscribe({
                        error: (error) => this.handleError('Error loading auto-import resources:', error),
                    });
            }
        }
    }

    public resetImportState(preserveSelectedItem?: ImportItem): void {
        this.clearResourceState();
        this.selectedItem = preserveSelectedItem || null;
        this.enableAutoImport = false;
        this.loading = false;

        if (!preserveSelectedItem) {
            this.importMode = this.importModes[0];
        }
    }

    private clearResourceState(): void {
        this.showResources = false;
        this.resources = [];
        this.selectedResources = [];
        this.totalResourceCount = 0;
    }

    public async handleImport(enableAutoImport?: boolean): Promise<void> {
        if (this.importBlockedByAuth || !this.selectedItem) return;

        const isAutoImportToggle = this.importMode?.value === ImportModeValues.AUTOMATIC;

        if (!isAutoImportToggle && this.selectedResources.length === 0) {
            return;
        }

        if (isAutoImportToggle) {
            // TODO call own function with separate logic and separate endpoint
            try {
                await firstValueFrom(
                    this.integrationService.configureAutoImport(
                        this.serviceIdentifier,
                        enableAutoImport!,
                        this.currentLayerId,
                        this.selectedItem.value,
                    ),
                );
                this.handleAutoImportToggleSuccess(enableAutoImport);
            } catch (error) {
                console.error('Error in configureAutoImport API request:', error);
                // Put the switch back so it cannot show a state the backend rejected.
                this.enableAutoImport = !enableAutoImport;
                this.showImportErrorFallback(error);
            }
            return;
        }

        this.loading = true;

        try {
            const importRequestForm: ResourceImportRequestForm = {
                layerId: this.currentLayerId,
                resourceType: this.selectedItem.value,
                resourceIds: this.selectedResources.map((contact) => contact.value) || [],
            };

            await firstValueFrom(this.integrationService.importResources(this.serviceIdentifier, importRequestForm));
            this.handleManualImportSuccess();
        } catch (error) {
            console.error('Error in importResources API request:', error);
            this.showImportErrorFallback(error);
        }

        this.loading = false;
    }

    private handleManualImportSuccess(): void {
        const importedResourceIds = this.selectedResources.map((r) => r.value);
        this.selectedResources = [];
        this.import.emit();
        this.refreshImportedResources(importedResourceIds);
        this.toastService.success('int_import_success_toast_header', 'int_import_success_toast_details');
    }

    private handleAutoImportToggleSuccess(enableAutoImport: boolean | undefined): void {
        this.loading = false;

        const suffix = enableAutoImport ? '_enabled' : '_disabled';

        this.toastService.success(
            'int_toggle_auto_import_success_message' + suffix,
            'int_toggle_auto_import_success_header' + suffix,
        );

        this.integrationService.loadIntegrations(true).subscribe();
    }

    private showErrorMessage(): void {
        this.toastService.error();
    }

    /**
     * Shows the generic message only if nobody else reported the failure. ReauthError hides
     * the response it wraps, and the global handler shows only API errors it can translate.
     */
    private showImportErrorFallback(error: unknown): void {
        const reported = error instanceof ReauthError ? error.originalError : error;
        if (TypeGuard.isHttpApiError(reported) && this.apiErrorHandler.canHandle(reported.error.error)) {
            return;
        }

        this.showErrorMessage();
    }

    onAutoImportChange(value: boolean): void {
        this.enableAutoImport = value;

        // Trigger import instantly when toggling the switch
        if (this.selectedItem) {
            void this.handleImport(value);
        }
    }

    onResourceSelChange(event: { value: Resource[] }): void {
        this.selectedResources = event.value;
    }

    /**
     * Selection of importSource dropdown
     * Handles the logic for selecting an import source (contacts or services).
     * @param event The selection event containing the selected import item.
     */
    onItemSelected(event: { value: ImportItem }): void {
        const selectedDataSource = event.value.value;

        if (this.hasAutoImportEnabled(selectedDataSource)) {
            this.handleAutoImportMode(event.value);
        } else {
            this.handleManualImportMode(event.value, selectedDataSource);
        }
    }

    private hasAutoImportEnabled(dataSource: ResourceTypes): boolean {
        return this.isImportEnabledForDataSource(this.currentLayerId, dataSource);
    }

    private handleAutoImportMode(selectedItem: ImportItem): void {
        this.resetImportState(selectedItem);
        this.setupAutoImportFromRoute();
        this.enableAutoImport = true;
    }

    private handleManualImportMode(selectedItem: ImportItem, dataSource: ResourceTypes): void {
        this.resetImportState(selectedItem);
        if (this.importBlockedByAuth) return;
        if (this.importMode?.value === ImportModeValues.MANUAL) {
            this.loading = true;
            this.loadResourcesForDataSourceObservable(dataSource)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    error: (error) => this.handleError('Error loading resources:', error),
                });
        }
    }

    /**
     * Refreshes current resources (manual refresh button)
     */
    public refreshCurrentResources(): void {
        if (!this.importBlockedByAuth && this.selectedItem?.value) {
            this.loading = true;
            this.loadResourcesForDataSourceObservable(this.selectedItem.value)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    error: (error) => this.handleError('Error refreshing resources:', error),
                });
        }
    }

    /**
     * Removes successfully imported resources from the local resource list
     * @param importedResourceIds Array of resource IDs that were successfully imported
     */
    private refreshImportedResources(importedResourceIds: string[]): void {
        if (importedResourceIds.length === 0) return;
        this.resources = this.resources.filter((resource) => !importedResourceIds.includes(resource.value));
    }

    private loadResourcesForDataSourceObservable(resourceType: ResourceTypes): Observable<Contact[]> {
        // TODO - check if the following condition can become true...
        if (!resourceType) {
            this.resetImportState();
            return new Observable((subscriber) => {
                this.loading = false;
                subscriber.complete();
            });
        }

        const serviceCall = this.integrationService.getResources(this.serviceIdentifier, resourceType);
        return serviceCall.pipe(
            takeUntil(this.destroy$),
            tap((response: ServiceResource[]) => this.handleResponse(response)),
        );
    }

    /**
     * Handles the response by filtering contacts/services and extracting names.
     * @param response The response from the service (contacts or services)
     */
    private handleResponse(response: ServiceResource[]): void {
        const filteredResources = this.filterNewResources(response);

        this.updateResourceState(response, filteredResources);
        this.finishLoading();
    }

    private filterNewResources(allResources: ServiceResource[]): Resource[] {
        return allResources
            .filter((resource) => !this.isResourceAlreadyImported(resource))
            .map((resource) => ({
                name: resource.name || '',
                value: resource.id || '',
            }))
            .filter((resource) => resource.name.trim() !== '');
    }

    private updateResourceState(allResources: ServiceResource[], filteredResources: Resource[]): void {
        this.resources = filteredResources;
        this.totalResourceCount = allResources.length;
    }

    private finishLoading(): void {
        this.loading = false;
        this.showResources = true;
        this.cdr.detectChanges();
    }

    /**
     * Checks if a resource is already imported based on its name.
     * @param resource The resource to check (contact or service)
     * @returns True if the resource is already imported, false otherwise
     */
    private isResourceAlreadyImported(resource: ServiceResource): boolean {
        const resourceName = resource.name || '';
        return this.allTiles.some(
            (tile) =>
                IntegrationImportComponent.normalizeName(tile.name) ===
                IntegrationImportComponent.normalizeName(resourceName),
        );
    }

    private static normalizeName(name: string): string {
        return name.toLowerCase().replace(/\s+/g, '');
    }

    private handleError(message: string, error: unknown): void {
        console.error(message, error);
        this.loading = false;
        this.showImportErrorFallback(error);
    }

    onHide(): void {
        this.resetImportState();
        this.showDialog = false;
    }

    // -----------helper methods ---------

    /**
     * Gets the layer name by its ID
     * @param layerId The ID of the layer to find
     * @returns The layer name or undefined if not found
     */
    private getLayerNameById(layerId?: number): string | undefined {
        if (!layerId) return undefined;
        return this.layers.find((layer) => layer.id === layerId)?.name;
    }

    /**
     * Gets auto-import layers from integration config
     * @returns Array of layer import configs or empty array
     */
    private getAutoImportLayers(): LayerImportConfig[] {
        // @ts-expect-error: TODO proper typing for integrationConfig.configuration
        return this.integrationConfig?.configuration.autoImport?.layers || [];
    }

    /**
     * Finds a conflicting layer that has auto-import enabled for the data source in current or other layers.
     * @param currentLayerId The current layer ID to exclude from search
     * @param selectedDataSource The data source type to check for conflicts
     * @returns The conflicting layer or null if none found
     */
    private findConflictingLayer(
        currentLayerId: number,
        selectedDataSource: ResourceTypes,
    ): LayerImportConfig | undefined | null {
        const autoImportLayers = this.getAutoImportLayers();
        if (autoImportLayers.length === 0) return null;

        const correspondingWebHookEventType = EventTypeMapper.convertImportTypeToWebHookEventType(selectedDataSource);
        if (!correspondingWebHookEventType) return null;

        return autoImportLayers.find(
            (layer) =>
                layer.autoImportEnabled &&
                // Check for conflicts in other layers with the same data source
                ((layer.importEventType === correspondingWebHookEventType && layer.layerId !== currentLayerId) ||
                    // Check for conflicts in the current layer with a different data source
                    (layer.importEventType !== correspondingWebHookEventType && layer.layerId === currentLayerId)),
        );
    }

    /**
     * Checks if auto-import is enabled for a specific data source in a given layer
     * @param layerId The layer ID to check
     * @param dataSource The data source type to check
     * @returns True if auto-import is enabled for the data source in the layer
     */
    private isImportEnabledForDataSource(layerId: number, dataSource: ResourceTypes): boolean {
        const autoImportLayers = this.getAutoImportLayers();
        return autoImportLayers.some(
            (layer) =>
                layer.layerId === layerId &&
                EventTypeMapper.convertWebHookEventTypeToImportType(
                    layer.importEventType as AutoImportWebHookEventTypes,
                ) === dataSource &&
                layer.autoImportEnabled,
        );
    }

    /**
     * Handles the import mode change event
     * @param event The mode change event
     */
    onImportModeChange(event: TypedSelectButtonChangeEvent<ImportMode>): void {
        this.importMode = event.value ?? null;

        // Load resources for manual mode if needed
        if (event.value?.value === ImportModeValues.MANUAL && this.selectedItem) {
            this.loading = true;
            this.loadResourcesForDataSourceObservable(this.selectedItem.value)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    error: (error) => this.handleError('Error loading resources for manual mode:', error),
                });
        }
    }

    /**
     * Gets conflict information for an import item
     * @param item The import item to check
     * @returns Object with conflict status and layer info, or null if no conflict
     */
    private getItemConflictInfo(item: ImportItem): { layerId: number; layerName: string } | null {
        const conflictingLayer = this.findConflictingLayer(this.currentLayerId, item.value);

        if (conflictingLayer && conflictingLayer.layerId !== this.currentLayerId) {
            return {
                layerId: conflictingLayer.layerId,
                layerName: this.getLayerNameById(conflictingLayer.layerId) || '',
            };
        }

        return null;
    }
}

// TODO remove mapper, this is only needed due to unnecessary webhook type, resource type is sufficient, this is lexware implementation detail
class EventTypeMapper {
    /**
     * Reverse mapping from webhook event types to import types
     */
    private static webHookImportTypeMapping = new Map([
        [AutoImportWebHookEventTypes.CONTACT_CREATED, ResourceTypes.CONTACTS],
        [AutoImportWebHookEventTypes.SERVICE_CREATED, ResourceTypes.SERVICES],
    ]);

    /**
     * Converts a Lexware webhook event type to a Lexware resource import type.
     * @param webHookEventType The webhook event type to convert.
     * @returns The corresponding resource import type.
     */
    public static convertWebHookEventTypeToImportType(
        webHookEventType: AutoImportWebHookEventTypes,
    ): ResourceTypes | null {
        const result = this.webHookImportTypeMapping.get(webHookEventType);
        if (!result) {
            console.error('Unsupported lexware webhook event type:', webHookEventType);
            return null;
        }
        return result;
    }

    /**
     * Bidirectional mapping between import types and webhook event types
     */
    private static importTypeWebHookMapping = new Map([
        [ResourceTypes.CONTACTS, AutoImportWebHookEventTypes.CONTACT_CREATED],
        [ResourceTypes.SERVICES, AutoImportWebHookEventTypes.SERVICE_CREATED],
    ]);

    /**
     * Converts a Lexware import type to a webhook event type.
     * @param importType The import type to convert.
     * @returns The corresponding webhook event type.
     */
    public static convertImportTypeToWebHookEventType(importType: ResourceTypes): AutoImportWebHookEventTypes | null {
        const result = this.importTypeWebHookMapping.get(importType);
        if (!result) {
            console.error('Unsupported lexware import type:', importType);
            return null;
        }
        return result;
    }
}
